import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { PageHero } from '@/components/shared/PageHero';
import { EmptyState } from '@/components/shared/EmptyState';
import { PROGRAMS } from '@/data/mockData';
import { formatDate } from '@/lib/storage';
import { Calendar, Clock, MapPin, Users, Search, Building2, ArrowRight } from 'lucide-react';

const CATEGORIES = ['All', ...Array.from(new Set(PROGRAMS.map((p) => p.category)))];

const Programs = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  const list = useMemo(() => {
    return PROGRAMS.filter((p) => {
      const matchCat = cat === 'All' || p.category === cat;
      const matchQ = (p.title + p.description + p.location).toLowerCase().includes(q.toLowerCase());
      return matchCat && matchQ;
    });
  }, [q, cat]);

  return (
    <div data-testid="programs-page">
      <PageHero
        eyebrow="Programs"
        title="Programs & Events"
        subtitle="Free community programs, training, sports leagues and outreach activities."
        breadcrumbs={[{ label: 'Programs' }]}
      />

      <section className="container-app section-padding">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search programs..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="programs-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-thin -mx-1 px-1">
            {CATEGORIES.map((c) => (
              <Button
                key={c}
                size="sm"
                variant={cat === c ? 'default' : 'outline'}
                onClick={() => setCat(c)}
                className={cat === c ? 'bg-primary text-primary-foreground' : ''}
                data-testid={`programs-filter-${c.replace(/\s|\//g, '-').toLowerCase()}`}
              >
                {c}
              </Button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <EmptyState icon={Calendar} title="No programs match" description="Try a different category or keyword." />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((p) => {
              const pct = Math.min(100, Math.round((p.registered / p.slots) * 100));
              const full = p.registered >= p.slots;
              return (
                <Link key={p.id} to={`/programs/${p.id}`} className="group block" data-testid={`program-${p.id}`}>
                  <Card className="h-full overflow-hidden card-elevated border-0">
                    <div className="aspect-[16/10] overflow-hidden bg-secondary relative">
                      {p.image ? (
                        <img src={p.image} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
                      ) : (
                        <Building2 className="h-10 w-10 text-primary/40 mx-auto" />
                      )}
                      <Badge className={`absolute top-3 left-3 ${full ? 'bg-destructive' : 'bg-success'} text-white border-0`}>
                        {full ? 'FULL' : 'OPEN'}
                      </Badge>
                    </div>
                    <CardContent className="p-5">
                      <Badge variant="secondary" className="bg-accent-soft text-accent hover:bg-accent-soft">{p.category}</Badge>
                      <h3 className="mt-3 font-heading font-semibold leading-snug line-clamp-2">{p.title}</h3>
                      <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                        <div className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{formatDate(p.date)}</div>
                        <div className="inline-flex items-center gap-1.5 ml-3"><Clock className="h-3.5 w-3.5" />{p.time}</div>
                        <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</div>
                      </div>
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground inline-flex items-center gap-1"><Users className="h-3 w-3" /> {p.registered}/{p.slots} registered</span>
                          <span className="font-semibold text-primary">{pct}%</span>
                        </div>
                        <Progress value={pct} className="mt-1.5 h-1.5" />
                      </div>
                      <div className="mt-4 inline-flex items-center text-xs font-semibold text-primary group-hover:gap-1.5 gap-1 transition-all">
                        View details <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Programs;
